import { useState, useEffect, useRef, useCallback } from "react";
import { useSnackbar } from "notistack";
import { useMapFlying } from "./useMapFlying";

export const useGeolocation = (mapRef: React.RefObject<any>) => {
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number; accuracy: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isTrackingLocation, setIsTrackingLocation] = useState(false);
  const locationWatchIdRef = useRef<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { flyTo } = useMapFlying(mapRef);

  const handleLocationRequest = useCallback(() => {
    if (isTrackingLocation) {
      // Stop tracking and zoom to current location
      if (locationWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }
      setIsTrackingLocation(false);

      // Zoom to current location if we have it, then hide the dot
      if (userLocation && mapRef.current) {
        flyTo({
          center: [userLocation.longitude, userLocation.latitude],
          zoom: 16,
          duration: 2000,
          essential: true
        });
      }

      // Hide the blue dot after stopping tracking
      setUserLocation(null);

      enqueueSnackbar("Location tracking stopped", {
        variant: "info",
        autoHideDuration: 2000
      });
      return;
    }

    if (!navigator.geolocation) {
      enqueueSnackbar("Geolocation is not supported by this browser", {
        variant: "error",
        autoHideDuration: 5000
      });
      return;
    }

    setIsGettingLocation(true);

    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 30000 // 30 seconds for continuous tracking
    };

    const handleLocationSuccess = (position: GeolocationPosition, shouldZoom: boolean = false) => {
      const { latitude, longitude, accuracy } = position.coords;

      // Validate coordinates
      if (
        typeof latitude !== "number" ||
        typeof longitude !== "number" ||
        latitude < -90 ||
        latitude > 90 ||
        longitude < -180 ||
        longitude > 180
      ) {
        enqueueSnackbar("Invalid location coordinates received", {
          variant: "error",
          autoHideDuration: 5000
        });
        setIsGettingLocation(false);
        return;
      }

      const isFirstLocation = !userLocation;
      setUserLocation({ latitude, longitude, accuracy });
      setIsGettingLocation(false);

      // Fly to user location when requested (button click) or first location
      if ((shouldZoom || isFirstLocation) && mapRef.current) {
        flyTo({
          center: [longitude, latitude],
          zoom: 16, // Close zoom level for user location
          duration: 2000,
          essential: true
        });
      }

      // Success messages removed - no need to show location found notifications
    };

    const handleLocationError = (error: GeolocationPositionError) => {
      setIsGettingLocation(false);
      setIsTrackingLocation(false);
      if (locationWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }

      // Hide the blue dot on error
      setUserLocation(null);

      let errorMessage = "Unable to get your location";

      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = "Location access was denied. Please enable location permissions.";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = "Location information is unavailable.";
          break;
        case error.TIMEOUT:
          errorMessage = "Location request timed out. Please try again.";
          break;
        default:
          errorMessage = `Location error: ${error.message}`;
          break;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 5000
      });
    };

    // First get current position, then start watching if successful
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationSuccess(position, true); // Always zoom on button click

        // Start continuous tracking
        setIsTrackingLocation(true);
        locationWatchIdRef.current = navigator.geolocation.watchPosition(
          (position) => handleLocationSuccess(position, false), // Don't zoom during tracking updates
          handleLocationError,
          options
        );

        enqueueSnackbar("Started tracking your location", {
          variant: "info",
          autoHideDuration: 3000
        });
      },
      handleLocationError,
      options
    );
  }, [flyTo, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      if (locationWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
      }
      setUserLocation(null);
      setIsTrackingLocation(false);
    };
  }, []);

  return { userLocation, isTrackingLocation, isGettingLocation, handleLocationRequest };
};
